import { useDebouncedValue } from '@mantine/hooks';
import { InfiniteListBase, SortPayload } from 'react-admin';
import { useMemo, PropsWithChildren } from 'react';
import { useCosmosQueryClient } from 'providers/clients/CosmosQueryClientProvider';
import { SearchView } from 'components/Search/SearchView';
import { SearchItemsProvider } from 'components/Search/components/SearchItemsProvider';
import { useSearch } from 'components/Search/SearchContextProvider';
import EditorialSearchKpiBar from './components/EditorialSearchKpiBar';
import { SavedSearchModal } from 'components/SavedSearch/SavedSearchModal';
import { BatchEditModal } from 'components/BatchEdit/BatchEditModal';
import { BatchEditContextProvider } from 'components/BatchEdit/context';
import { EditorialSearchFooter } from 'components/Search/components/SearchFooter';
import { SearchViewResults } from 'components/Search/components/SearchAdvance/SearchAdvancedView';
import { SearchAdvancedTopBar } from 'components/Search/components/SearchAdvance/SearchAdvancedTopBar';
import { EditorialPreSearchContent } from 'components/Search/components/SearchAdvance/components/Editorial/EditorialPreSearchContent';
import { EditorialPostSearchContent } from 'components/Search/components/SearchAdvance/components/Editorial/EditorialPostSearchContent';
import { EditorialEndSearchContent } from 'components/Search/components/SearchAdvance/components/Editorial/EditorialEndSearchContent';
import { EditorialPopoverFields } from 'components/Search/components/SearchAdvance/components/Editorial/EditorialPopoverFields';
import { EditorialFilterFields } from 'components/Search/components/SearchAdvance/components/Editorial/EditorialFilterFields';
import { useCosmosEditorial } from 'providers/contexts/CosmosEditorialContext';
import { AnyRecord } from 'utility/types/cosmosTypes';
import { checkSearchEnabled } from 'components/Search/functions';

export function sanitizeEditorialFilter<T extends AnyRecord>(filter: T): T {
  const sanitizedFilter: AnyRecord = { ...filter };

  Object.keys(sanitizedFilter).forEach((key) => {
    const value = sanitizedFilter[key];
    // rel CMSADMIN-1049: trim for 'query' field
    if (key === 'query' && typeof value === 'string') {
      sanitizedFilter[key] = value.trim();
    }
    // trim semantic param
    if (key === 'semantic' && typeof value === 'string') {
      sanitizedFilter[key] = value.trim();
    }
    // trim print_slug: api errors if all spaces
    if (key === 'print_slug' && typeof value === 'string') {
      sanitizedFilter[key] = value.trim();
    }
  });

  return sanitizedFilter as T;
}

export const EditorialSearchView = ({ children }: PropsWithChildren) => {
  const { queryClient } = useCosmosQueryClient();
  const { validListFilter } = useSearch();
  const { editorialConfig } = useCosmosEditorial();
  const [debouncedListFilter] = useDebouncedValue(validListFilter, 400);

  const {
    sort: debouncedSort,
    dir: debouncedOrder,
    ...debouncedFilter
  } = debouncedListFilter;

  // Sanitize fields in payload
  const sanitizedFilter = useMemo(() => {
    const sanitized = sanitizeEditorialFilter(debouncedFilter);
    const final = {
      ...sanitized,
      sort: debouncedSort,
      dir: debouncedOrder,
    };
    return final;
  }, [debouncedFilter, debouncedSort, debouncedOrder]);

  return (
    <InfiniteListBase
      resource="editorial"
      disableAuthentication={false}
      disableSyncWithLocation={true} // not using RA UI Components, handle it ourselves.
      storeKey={false} // not using RA UI Components, handle it ourselves.
      exporter={false}
      perPage={50}
      sort={
        {
          field: debouncedSort,
          order: debouncedOrder,
        } as SortPayload
      }
      filter={sanitizedFilter}
      queryOptions={{
        enabled: checkSearchEnabled({ filter: sanitizedFilter, resource: 'editorial' })
          .isEnabled,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        onSettled: (data, error) => {
          // react-admin query-cache-clearing for the 'partial record' issue
          // RA assumes our getList() returns a subset of getOne() data
          // so it'll try to preload the getOne() for edit screens
          // with the data from the getList() responses
          queryClient.removeQueries({ queryKey: ['editorial', 'getOne'] });
        },
      }}
    >
      <SearchItemsProvider>
        <BatchEditContextProvider>
          <SearchView
            kpiBar={<EditorialSearchKpiBar />}
            searchInputs={
              <SearchAdvancedTopBar
                preSearchComponent={<EditorialPreSearchContent />}
                postSearchComponent={<EditorialPostSearchContent />}
                endSearchComponent={<EditorialEndSearchContent />}
                advancedPopoverFieldsComponent={<EditorialPopoverFields />}
                advancedFilterFieldsComponent={<EditorialFilterFields />}
              />
            }
            searchResults={<SearchViewResults editorialConfig={editorialConfig} />}
            savedSearchModal={
              <SavedSearchModal
                addPayload={{
                  query: {
                    app: ['editorial'],
                  },
                }}
              />
            }
            batchEditModal={<BatchEditModal />}
            footerBar={<EditorialSearchFooter />}
          >
            {children}
          </SearchView>
        </BatchEditContextProvider>
      </SearchItemsProvider>
    </InfiniteListBase>
  );
};
